import React from 'react';
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
} from '@roger/ui-components';
import { HelpCircle } from 'lucide-react';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useTakeOverAvailable } from '../../hooks/useTakeOverAvailable';
import type { AgencyConfigPayload } from '../../services/agencyConfig';
import type { AgencyConfigUpdate } from '../../services/api/types.gen';
import { getBusinessToday } from '../../utils/dateUtils';
import { useAgencyConfigSection } from '../../hooks/useAgencyConfigSection';
import {
  SettingDateValue,
  SettingsEditFooter,
  SettingValue,
} from './SettingsSectionParts';

// Copy in the chart register (EMR-2888, Daniel 2026-10-15).
export const LAST_DAY_LABEL = 'Last day in the External EMR';
export const LAST_DAY_HELP =
  'The last day anyone charts in the External EMR. Roger proposes Take Over dates from it. Left blank, Roger proposes from today: ended periods stay with the External EMR, the rest move to Roger.';
export const LAST_DAY_PASSED = 'This day has passed.';
export const LAG_LABEL =
  'Days the External EMR needs to bill a finished claim period';
export const LAG_HELP =
  'After a claim period ends, the External EMR needs this many days to bill it. A period it cannot bill before the last day moves to Roger.';
export const LAG_INVALID = 'Enter 0 to 60 days.';
export const LOCK_LABEL = 'Lock imported charts until they move to Roger';
export const LOCK_HELP =
  'On, an imported chart cannot be charted in Roger until Take Over. Off, staff may edit it at once. Roger keeps their edits when the External EMR changes the same row.';

const LAG_MAX = 60;
const DEFAULT_LAG = 5;

type Values = {
  lastDay: string | null;
  lag: string;
  lock: boolean;
  // The last day is an onboarding date, editable on the go-live date's rule.
  canEditLastDay: boolean;
};

const DEFAULTS: Values = {
  lastDay: null,
  lag: String(DEFAULT_LAG),
  lock: true,
  canEditLastDay: false,
};

function lagDays(lag: string): number | null {
  if (!/^\d+$/.test(lag.trim())) return null;
  const days = Number(lag);
  return days <= LAG_MAX ? days : null;
}

function fromConfig(cfg: AgencyConfigPayload): Values {
  return {
    lastDay: cfg.external_emr_last_day ?? null,
    lag: String(cfg.external_emr_billing_lag_days ?? DEFAULT_LAG),
    lock: cfg.external_emr_lock_imported_charts ?? true,
    canEditLastDay: cfg.can_edit_onboarding_dates ?? false,
  };
}

function diff(values: Values, saved: Values): AgencyConfigUpdate {
  const body: AgencyConfigUpdate = {};
  const lag = lagDays(values.lag);
  if (values.lastDay !== saved.lastDay) {
    body.external_emr_last_day = values.lastDay;
  }
  if (lag !== null && lag !== lagDays(saved.lag)) {
    body.external_emr_billing_lag_days = lag;
  }
  if (values.lock !== saved.lock) {
    body.external_emr_lock_imported_charts = values.lock;
  }
  return body;
}

function afterSave(values: Values): Values {
  return { ...values, lag: String(lagDays(values.lag) ?? DEFAULT_LAG) };
}

const Help: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type='button'
        className='inline-flex shrink-0 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        aria-label={`About ${label}`}
      >
        <HelpCircle className='h-4 w-4' />
      </button>
    </PopoverTrigger>
    <PopoverContent className='w-72 max-w-[calc(100vw-2rem)] text-sm'>
      {text}
    </PopoverContent>
  </Popover>
);

/** One setting: its label, a help button, an optional note, and the control. */
const SettingRow: React.FC<{
  id: string;
  testId: string;
  label: string;
  help: string;
  note?: React.ReactNode;
  stacked?: boolean;
  children: React.ReactNode;
}> = ({ id, testId, label, help, note, stacked = false, children }) => (
  <div
    data-testid={testId}
    className={
      stacked
        ? 'flex flex-col gap-3 rounded-md border p-3 md:flex-row md:items-center md:justify-between md:gap-4'
        : 'flex items-center justify-between gap-4 rounded-md border p-3'
    }
  >
    <div className='space-y-1'>
      <div className='flex items-center gap-2'>
        <label htmlFor={id} className='text-sm font-medium'>
          {label}
        </label>
        <Help label={label} text={help} />
      </div>
      {note}
    </div>
    {children}
  </div>
);

/**
 * The agency's External EMR settings: the last day anyone charts there, the
 * days it needs to bill a finished claim period, and the chart lock. All three
 * act only under Take Over v2, so the section shows only where it is on.
 */
const ExternalEmrSection: React.FC = () => {
  const takeOverAvailable = useTakeOverAvailable();
  const { canEdit } = useAdminSectionAccess('onboarding');
  const section = useAgencyConfigSection({
    enabled: takeOverAvailable,
    initial: DEFAULTS,
    fromConfig,
    diff,
    afterSave,
    successMessage: 'External EMR settings updated',
    errorMessage: 'Failed to update External EMR settings',
  });
  if (!takeOverAvailable) return null;

  const { values, setValues, isLoading, loadFailed } = section;
  const editing = section.isEditing && canEdit;
  const editingLastDay = section.isEditing && values.canEditLastDay;
  const lag = lagDays(values.lag);
  const lastDayPassed =
    values.lastDay !== null && values.lastDay < getBusinessToday();
  const valueProps = { isLoading, loadFailed };

  return (
    <section className='space-y-4' aria-labelledby='external-emr-heading'>
      <div className='space-y-1'>
        <h3 id='external-emr-heading' className='text-base font-semibold'>
          External EMR
        </h3>
        <p className='text-sm text-muted-foreground'>
          How charts move from the External EMR to Roger
        </p>
        {loadFailed && (
          <p className='text-xs text-destructive'>
            These settings did not load. Reload the page to see them.
          </p>
        )}
      </div>

      <SettingRow
        id='external-emr-last-day'
        testId='setting-external_emr_last_day'
        label={LAST_DAY_LABEL}
        help={LAST_DAY_HELP}
        stacked
        note={
          lastDayPassed && (
            <p className='text-xs text-amber-700' role='status'>
              {LAST_DAY_PASSED}
            </p>
          )
        }
      >
        <SettingDateValue
          id='external-emr-last-day'
          label={LAST_DAY_LABEL}
          value={values.lastDay}
          editing={editingLastDay}
          {...valueProps}
          onChange={lastDay => setValues(prev => ({ ...prev, lastDay }))}
        />
      </SettingRow>

      <SettingRow
        id='external-emr-lag'
        testId='setting-external_emr_billing_lag_days'
        label={LAG_LABEL}
        help={LAG_HELP}
        stacked
        note={
          editing &&
          lag === null && (
            <p className='text-xs text-destructive' role='alert'>
              {LAG_INVALID}
            </p>
          )
        }
      >
        <SettingValue {...valueProps}>
          {editing ? (
            <Input
              id='external-emr-lag'
              type='number'
              inputMode='numeric'
              min={0}
              max={LAG_MAX}
              inputSize='sm'
              className='w-full md:w-24'
              value={values.lag}
              aria-invalid={lag === null}
              onChange={event =>
                setValues(prev => ({ ...prev, lag: event.target.value }))
              }
            />
          ) : (
            <span className='shrink-0 whitespace-nowrap text-sm font-medium'>
              {values.lag} {values.lag === '1' ? 'day' : 'days'}
            </span>
          )}
        </SettingValue>
      </SettingRow>

      <SettingRow
        id='external-emr-lock'
        testId='setting-external_emr_lock_imported_charts'
        label={LOCK_LABEL}
        help={LOCK_HELP}
      >
        <SettingValue {...valueProps}>
          {editing ? (
            <Switch
              id='external-emr-lock'
              checked={values.lock}
              onCheckedChange={checked =>
                setValues(prev => ({ ...prev, lock: checked }))
              }
            />
          ) : (
            <span
              className={`shrink-0 whitespace-nowrap text-sm font-medium ${
                values.lock ? 'text-green-600' : 'text-muted-foreground'
              }`}
            >
              {values.lock ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </SettingValue>
      </SettingRow>

      <SettingsEditFooter
        canEdit={canEdit || values.canEditLastDay}
        isEditing={section.isEditing}
        isSaving={section.isSaving}
        editDisabled={isLoading || loadFailed}
        saveDisabled={lag === null}
        onEdit={section.startEditing}
        onCancel={section.cancel}
        onSave={() => void section.save()}
      />
    </section>
  );
};

export default ExternalEmrSection;
